import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  Archive,
  GripVertical,
  Package,
  ExternalLink,
  Copy,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  published: { label: "Published", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  draft: { label: "Draft", color: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20" },
  archived: { label: "Archived", color: "bg-[#333]/50 text-[#888] border-[#333]" },
};

export default function AdminEvents() {
  const { admin } = useAdminAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [duplicating, setDuplicating] = useState(null);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/events`, {
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      const data = await res.json();
      setEvents(data);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  }, [admin.token]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleStatusChange = async (eventId, newStatus) => {
    try {
      await fetch(`${API_URL}/api/admin/events/${eventId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      fetchEvents();
    } catch (err) {
      console.error("Status update failed:", err);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Delete this event and all its products? This cannot be undone.")) return;
    setDeleting(eventId);
    try {
      await fetch(`${API_URL}/api/admin/events/${eventId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      fetchEvents();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setDeleting(null);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(events);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setEvents(reordered);

    try {
      await fetch(`${API_URL}/api/admin/events/reorder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${admin.token}`,
        },
        body: JSON.stringify({ orderedIds: reordered.map((e) => e.id) }),
      });
    } catch (err) {
      console.error("Reorder failed:", err);
      fetchEvents();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-events-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-white text-2xl font-sans font-semibold">Events</h1>
          <p className="text-[#666] text-sm font-sans mt-1">
            Manage your shopping events. Drag to reorder.
          </p>
        </div>
        <button
          onClick={() => navigate("/admin/events/new")}
          className="flex items-center gap-2 bg-[#D8C08F] text-black font-semibold px-4 py-2.5 rounded-lg text-sm font-sans hover:bg-[#c5ad7d] transition-colors"
          data-testid="create-event-button"
        >
          <Plus size={16} />
          New Event
        </button>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="events-list">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="space-y-3"
              data-testid="events-list"
            >
              {events.map((event, index) => {
                const status = statusConfig[event.status] || statusConfig.draft;
                return (
                  <Draggable key={event.id} draggableId={event.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4 flex items-center gap-4 transition-shadow ${
                          snapshot.isDragging ? "shadow-xl shadow-black/50 border-[#D8C08F]/30" : ""
                        }`}
                        data-testid={`event-row-${event.id}`}
                      >
                        {/* Drag handle */}
                        <div
                          {...provided.dragHandleProps}
                          className="cursor-grab active:cursor-grabbing text-[#555] hover:text-[#888]"
                          data-testid={`drag-handle-${event.id}`}
                        >
                          <GripVertical size={18} />
                        </div>

                        {/* Image thumbnail */}
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#111] shrink-0">
                          {event.image ? (
                            <img
                              src={event.image}
                              alt={event.brand}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-[#333]">
                              <Package size={20} />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-white text-sm font-sans font-medium truncate">
                              {event.brand}
                            </h3>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-sans font-medium border ${status.color}`}
                            >
                              {status.label}
                            </span>
                          </div>
                          {event.badge && (
                            <p className="text-[#D8C08F] text-xs font-sans">{event.badge}</p>
                          )}
                          <p className="text-[#555] text-xs font-sans">{event.subtitle}</p>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          {event.status === "draft" && (
                            <button
                              onClick={() => handleStatusChange(event.id, "published")}
                              className="p-2 rounded-lg hover:bg-emerald-500/10 text-[#555] hover:text-emerald-400 transition-colors"
                              title="Publish"
                              data-testid={`publish-${event.id}`}
                            >
                              <Eye size={16} />
                            </button>
                          )}
                          {event.status === "published" && (
                            <button
                              onClick={() => handleStatusChange(event.id, "draft")}
                              className="p-2 rounded-lg hover:bg-yellow-500/10 text-[#555] hover:text-yellow-400 transition-colors"
                              title="Unpublish"
                              data-testid={`unpublish-${event.id}`}
                            >
                              <EyeOff size={16} />
                            </button>
                          )}
                          {event.status !== "archived" && (
                            <button
                              onClick={() => handleStatusChange(event.id, "archived")}
                              className="p-2 rounded-lg hover:bg-[#333]/50 text-[#555] hover:text-[#888] transition-colors"
                              title="Archive"
                              data-testid={`archive-${event.id}`}
                            >
                              <Archive size={16} />
                            </button>
                          )}
                          {event.status === "archived" && (
                            <button
                              onClick={() => handleStatusChange(event.id, "draft")}
                              className="p-2 rounded-lg hover:bg-yellow-500/10 text-[#555] hover:text-yellow-400 transition-colors"
                              title="Restore to Draft"
                              data-testid={`restore-${event.id}`}
                            >
                              <EyeOff size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => navigate(`/admin/events/${event.id}/preview`)}
                            className="p-2 rounded-lg hover:bg-[#D8C08F]/10 text-[#555] hover:text-[#D8C08F] transition-colors"
                            title="Preview"
                            data-testid={`preview-${event.id}`}
                          >
                            <ExternalLink size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/events/${event.id}/products`)}
                            className="p-2 rounded-lg hover:bg-blue-500/10 text-[#555] hover:text-blue-400 transition-colors"
                            title="Manage Products"
                            data-testid={`products-${event.id}`}
                          >
                            <Package size={16} />
                          </button>
                          <button
                            onClick={async () => {
                              setDuplicating(event.id);
                              try {
                                await fetch(`${API_URL}/api/admin/events/${event.id}/duplicate`, {
                                  method: "POST",
                                  headers: { Authorization: `Bearer ${admin.token}` },
                                });
                                fetchEvents();
                              } catch (err) {
                                console.error("Duplicate failed:", err);
                              } finally {
                                setDuplicating(null);
                              }
                            }}
                            disabled={duplicating === event.id}
                            className="p-2 rounded-lg hover:bg-[#D8C08F]/10 text-[#555] hover:text-[#D8C08F] transition-colors disabled:opacity-50"
                            title="Duplicate Event"
                            data-testid={`duplicate-${event.id}`}
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            onClick={() => navigate(`/admin/events/${event.id}/edit`)}
                            className="p-2 rounded-lg hover:bg-white/5 text-[#555] hover:text-white transition-colors"
                            title="Edit"
                            data-testid={`edit-${event.id}`}
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(event.id)}
                            disabled={deleting === event.id}
                            className="p-2 rounded-lg hover:bg-red-500/10 text-[#555] hover:text-red-400 transition-colors disabled:opacity-50"
                            title="Delete"
                            data-testid={`delete-${event.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {events.length === 0 && (
        <div className="text-center py-16">
          <CalendarDays size={40} className="text-[#333] mx-auto mb-4" />
          <p className="text-[#666] text-sm font-sans">No events yet. Create your first event.</p>
        </div>
      )}
    </div>
  );
}
